/**
 * SupplierBookingLinkModal ✦
 *
 * The composition surface for a Supplier Studio booking link. Opens
 * from either the Bookings ledger or the Calendar — both surfaces
 * mount the same modal with the same backend flow.
 *
 * Stages:
 *   1. compose — package + T&Cs + optional note
 *   2. ready   — show generated `/book/:token` URL with copy +
 *                share actions, and an inline "Send another" pivot
 *
 * Editorial restraint:
 *   • Calm dark surface, no SaaS chrome
 *   • Save-as-default tick that quietly prefills next time
 *   • Single primary action per stage
 *
 * Out of scope (deferred): payments, e-signatures, branded
 * templates, email automation.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Copy, CheckCircle, X, Sparkle, WhatsappLogo, EnvelopeSimple, ShareNetwork } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PUBLIC_BOOK_URL = (token) => {
  // Honour the user's deployment domain — same origin as the
  // running frontend, so dev/preview/production all "just work".
  if (typeof window === 'undefined') return `/book/${token}`;
  return `${window.location.origin}/book/${token}`;
};

const SupplierBookingLinkModal = ({ open, onClose, onCreated, prefillDate }) => {
  const [stage, setStage] = useState('compose'); // compose | ready
  const [packageName, setPackageName] = useState('');
  const [terms, setTerms] = useState('');
  const [note, setNote] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  // Templates ✦ — supplier-saved presets. Distinct from the
  // single "default terms" pin; templates let suppliers juggle
  // multiple packages.
  const [templates, setTemplates] = useState([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  // Captured at prefill so the share copy can read
  // "from {business_name}" without an extra round-trip.
  const [businessName, setBusinessName] = useState('');

  // Prefill defaults — only once on open
  useEffect(() => {
    if (!open) return;
    setStage('compose');
    setError('');
    setCreated(null);
    setCopied(false);
    setSavedTemplateId(null);
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/studio/booking-defaults`).catch(() => ({ data: {} })),
      axios.get(`${API}/studio/booking-templates`).catch(() => ({ data: { templates: [] } })),
    ]).then(([defs, tpls]) => {
      if (cancelled) return;
      if (defs.data?.default_booking_terms) setTerms(defs.data.default_booking_terms);
      if (defs.data?.business_name) setBusinessName(defs.data.business_name);
      setTemplates(tpls.data?.templates || []);
    });
    return () => { cancelled = true; };
  }, [open]);

  const applyTemplate = (tpl) => {
    if (!tpl) return;
    setPackageName(tpl.package_name || '');
    setTerms(tpl.terms_text || '');
    setNote(tpl.supplier_note || '');
    setSavedTemplateId(tpl.id);
    setError('');
  };

  const saveAsNewTemplate = async () => {
    const name = (window.prompt('Name this template (e.g. "Wedding · 3 hours")') || '').trim();
    if (!name) return;
    setSavingTemplate(true);
    try {
      const r = await axios.post(`${API}/studio/booking-templates`, {
        name,
        package_name: packageName.trim(),
        terms_text: terms.trim(),
        supplier_note: note.trim(),
      });
      setTemplates((cur) => [r.data.template, ...cur]);
      setSavedTemplateId(r.data.template.id);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save the template");
    } finally {
      setSavingTemplate(false);
    }
  };

  if (!open) return null;

  const submit = async () => {
    setError('');
    if (!packageName.trim()) { setError('Name the package'); return; }
    if (!terms.trim())       { setError('Add booking terms'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/studio/booking-links`, {
        package_name: packageName.trim(),
        terms_text: terms.trim(),
        supplier_note: note.trim(),
        save_as_default: saveAsDefault,
      });
      setCreated(r.data.booking_link);
      setStage('ready');
      onCreated && onCreated(r.data.booking_link);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't compose the link");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(PUBLIC_BOOK_URL(created.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  };

  const sendAnother = () => {
    setPackageName('');
    setNote('');
    setCreated(null);
    setStage('compose');
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="booking-link-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(6,4,2,0.78)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 540,
          background: '#100A04',
          border: '1px solid rgba(194,157,82,0.22)',
          borderRadius: 6,
          padding: 'clamp(22px, 4vw, 36px)',
          color: '#F4E9D2',
          fontFamily: 'Georgia, serif',
          position: 'relative',
        }}
      >
        <button
          onClick={onClose}
          data-testid="booking-link-close"
          aria-label="Close"
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'transparent', border: 'none',
            color: 'rgba(244,233,210,0.55)', cursor: 'pointer',
            padding: 6, lineHeight: 0,
          }}
        >
          <X size={18} />
        </button>

        {stage === 'compose' && (
          <div>
            <p style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: 'rgba(194,157,82,0.78)',
              margin: 0,
            }}>✦ Send Booking Link</p>
            <h2 style={{
              fontFamily: 'Georgia, serif', margin: '8px 0 4px',
              fontWeight: 500, fontSize: 'clamp(22px, 4.4vw, 28px)',
              letterSpacing: '-0.012em',
            }}>Compose a quiet invitation</h2>
            <p style={{
              margin: 0, fontStyle: 'italic',
              fontSize: 'clamp(12px, 3.2vw, 13px)',
              color: 'rgba(244,233,210,0.55)',
            }}>Share a private link. The client fills in the rest — and the night appears in your diary on its own.</p>

            {/* Templates ✦ — quiet horizontal strip; only renders when
                the supplier has actually saved one. Restraint: no
                "Create new template" button here. Saving happens
                inline via the Save link below the form. */}
            {templates.length > 0 && (
              <div data-testid="booking-link-templates" style={{
                marginTop: 18, paddingTop: 14,
                borderTop: '1px solid rgba(244,233,210,0.08)',
              }}>
                <p style={{
                  fontSize: 10, fontWeight: 800, letterSpacing: '0.24em',
                  textTransform: 'uppercase', color: 'rgba(244,233,210,0.42)',
                  margin: 0,
                }}>Templates ✦</p>
                <div style={{
                  display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10,
                }}>
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => applyTemplate(tpl)}
                      data-testid={`booking-link-template-${tpl.id}`}
                      style={{
                        background: savedTemplateId === tpl.id
                          ? 'rgba(194,157,82,0.18)'
                          : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${savedTemplateId === tpl.id ? 'rgba(194,157,82,0.55)' : 'rgba(244,233,210,0.14)'}`,
                        color: savedTemplateId === tpl.id ? '#F4E9D2' : 'rgba(244,233,210,0.72)',
                        padding: '6px 12px', borderRadius: 999,
                        fontFamily: 'Georgia, serif',
                        fontStyle: 'italic', fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      {tpl.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Package or service">
                <input
                  data-testid="booking-link-package"
                  type="text"
                  value={packageName}
                  onChange={(e) => setPackageName(e.target.value)}
                  placeholder="e.g. Photo Booth · 3 hours"
                  maxLength={140}
                  style={inputStyle}
                />
              </Field>
              <Field label="Booking terms & conditions" hint="Cancellation, deposit, balance — anything the client should acknowledge.">
                <textarea
                  data-testid="booking-link-terms"
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={6}
                  maxLength={6000}
                  style={{ ...inputStyle, fontFamily: 'Georgia, serif', resize: 'vertical', lineHeight: 1.5 }}
                />
              </Field>
              <Field label="Personal note · optional" hint="A line to settle the booking softly.">
                <textarea
                  data-testid="booking-link-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={800}
                  placeholder="Looking forward to your night ✦"
                  style={{ ...inputStyle, fontFamily: 'Georgia, serif', fontStyle: 'italic', resize: 'vertical' }}
                />
              </Field>

              <label style={{
                display: 'flex', alignItems: 'center', gap: 10,
                fontSize: 12.5, color: 'rgba(244,233,210,0.72)',
                cursor: 'pointer', userSelect: 'none',
              }}>
                <input
                  type="checkbox"
                  data-testid="booking-link-save-default"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  style={{ accentColor: '#C29D52' }}
                />
                Save as my default terms ✦
              </label>

              <button
                type="button"
                onClick={saveAsNewTemplate}
                disabled={savingTemplate || !packageName.trim() || !terms.trim()}
                data-testid="booking-link-save-as-template"
                style={{
                  background: 'transparent', border: 'none',
                  color: 'rgba(194,157,82,0.78)',
                  fontStyle: 'italic', fontSize: 12,
                  cursor: (savingTemplate || !packageName.trim() || !terms.trim()) ? 'not-allowed' : 'pointer',
                  textAlign: 'left', padding: 0,
                  fontFamily: 'Georgia, serif',
                  textDecoration: 'underline', textUnderlineOffset: 3,
                  opacity: (savingTemplate || !packageName.trim() || !terms.trim()) ? 0.4 : 1,
                }}
              >
                {savingTemplate ? 'Saving template…' : 'Save these as a new template ✦'}
              </button>

              {error && (
                <div style={{
                  fontSize: 12.5, color: '#E08F8F', fontStyle: 'italic',
                }} data-testid="booking-link-error">{error}</div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <button
                  onClick={onClose}
                  data-testid="booking-link-cancel"
                  style={ghostBtn}
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={busy}
                  data-testid="booking-link-submit"
                  style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}
                >
                  {busy ? 'Composing…' : 'Compose the link ✦'}
                </button>
              </div>
            </div>
          </div>
        )}

        {stage === 'ready' && created && (
          <div data-testid="booking-link-ready">
            <p style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.32em',
              textTransform: 'uppercase', color: 'rgba(194,157,82,0.78)',
              margin: 0,
            }}>✦ Ready to send</p>
            <h2 style={{
              fontFamily: 'Georgia, serif', margin: '8px 0 4px',
              fontWeight: 500, fontSize: 'clamp(22px, 4.4vw, 28px)',
            }}>The invitation has settled</h2>
            <p style={{
              margin: 0, fontStyle: 'italic',
              fontSize: 'clamp(12px, 3.2vw, 13px)',
              color: 'rgba(244,233,210,0.55)',
            }}>Share this link with your client. It stays open for 30 days, or until you revoke it.</p>

            <div style={{
              marginTop: 22, padding: '14px 16px',
              background: '#070504',
              border: '1px solid rgba(244,233,210,0.1)',
              borderRadius: 4, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <Sparkle size={14} weight="fill" style={{ color: 'rgba(194,157,82,0.7)', flexShrink: 0 }} />
              <code
                data-testid="booking-link-url"
                style={{
                  flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  fontSize: 12, color: 'rgba(244,233,210,0.85)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >{PUBLIC_BOOK_URL(created.token)}</code>
              <button
                onClick={copyLink}
                data-testid="booking-link-copy"
                style={{
                  background: 'transparent', border: '1px solid rgba(194,157,82,0.4)',
                  color: '#C29D52', padding: '6px 10px', borderRadius: 999,
                  fontStyle: 'italic', fontSize: 11, cursor: 'pointer',
                  fontFamily: 'Georgia, serif',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {copied ? <><CheckCircle size={12} weight="fill" /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>

            {/* Share affordances ✦ — WhatsApp / Email / native share.
                The supplier doesn't enter the client's contact in the
                modal, so these open the share surface WITHOUT a
                pre-selected recipient. The supplier picks the
                contact from their phone book / mail client.
                Editorial restraint: no contact-import, no
                scheduled-send, no read receipts. */}
            <div style={{
              marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 8,
            }}>
              <ShareButton
                testId="booking-link-share-whatsapp"
                href={`https://wa.me/?text=${encodeURIComponent(composeShareMessage(businessName, PUBLIC_BOOK_URL(created.token)))}`}
                icon={<WhatsappLogo size={13} weight="fill" />}
              >Share on WhatsApp</ShareButton>
              <ShareButton
                testId="booking-link-share-email"
                href={`mailto:?subject=${encodeURIComponent(`Your booking invitation from ${businessName || 'EventDrop'} ✦`)}&body=${encodeURIComponent(composeShareMessage(businessName, PUBLIC_BOOK_URL(created.token)))}`}
                icon={<EnvelopeSimple size={13} />}
              >Share by email</ShareButton>
              {typeof navigator !== 'undefined' && navigator.share && (
                <button
                  data-testid="booking-link-share-native"
                  onClick={() => {
                    navigator.share({
                      title: `Booking with ${businessName || 'EventDrop'}`,
                      text: composeShareMessage(businessName, PUBLIC_BOOK_URL(created.token)),
                      url: PUBLIC_BOOK_URL(created.token),
                    }).catch(() => {});
                  }}
                  style={shareBtnStyle}
                >
                  <ShareNetwork size={13} /> More…
                </button>
              )}
            </div>

            {prefillDate && (
              <p style={{
                marginTop: 16, fontStyle: 'italic',
                fontSize: 12, color: 'rgba(244,233,210,0.5)',
              }}>For {prefillDate} — once accepted, the night will land on this date.</p>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
              <button
                onClick={sendAnother}
                data-testid="booking-link-send-another"
                style={ghostBtn}
              >
                Send another ✦
              </button>
              <button
                onClick={onClose}
                data-testid="booking-link-done"
                style={primaryBtn}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: 10, fontWeight: 800,
      letterSpacing: '0.24em', textTransform: 'uppercase',
      color: 'rgba(244,233,210,0.55)', marginBottom: 6,
    }}>{label}</label>
    {children}
    {hint && (
      <p style={{
        margin: '6px 0 0', fontStyle: 'italic',
        fontSize: 11.5, color: 'rgba(244,233,210,0.42)',
      }}>{hint}</p>
    )}
  </div>
);

const inputStyle = {
  width: '100%',
  background: '#070504',
  border: '1px solid rgba(244,233,210,0.14)',
  color: '#F4E9D2',
  padding: '11px 13px',
  borderRadius: 3,
  fontSize: 13.5,
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtn = {
  flex: 1,
  background: '#C29D52',
  color: '#100A04',
  border: '1px solid #C29D52',
  padding: '11px 16px',
  borderRadius: 3,
  fontFamily: 'Georgia, serif',
  fontStyle: 'italic',
  fontSize: 14,
  cursor: 'pointer',
};

const ghostBtn = {
  flex: 1,
  background: 'transparent',
  color: 'rgba(244,233,210,0.72)',
  border: '1px solid rgba(244,233,210,0.16)',
  padding: '11px 16px',
  borderRadius: 3,
  fontFamily: 'Georgia, serif',
  fontStyle: 'italic',
  fontSize: 14,
  cursor: 'pointer',
};

const shareBtnStyle = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(244,233,210,0.14)',
  color: 'rgba(244,233,210,0.85)',
  padding: '7px 12px',
  borderRadius: 999,
  fontStyle: 'italic',
  fontSize: 11.5,
  fontFamily: 'Georgia, serif',
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const ShareButton = ({ href, icon, children, testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    style={shareBtnStyle}
  >
    {icon} {children}
  </a>
);

// Calm editorial share message — kept short so it composes well in
// WhatsApp's text-only preview and doesn't bloat email subject lines.
const composeShareMessage = (businessName, url) => {
  const from = businessName ? ` from ${businessName}` : '';
  return `Here's your booking invitation${from} ✦\n\n${url}\n\nSettle whenever you're ready.`;
};

export default SupplierBookingLinkModal;
